<template>
  <NewBsBox
    :bs="{
      'cmp.textarea': 'wrapper',
      ...bs,
    }"
  >
    <NewBsBox
      ref="inputRef"
      :data-error="exceedsLength || undefined"
      :bs="{
        'cmp.textarea': 'input',
        minHeight: `${minTextAreaHeight}px`,
        border: readonly ? 'none' : null,
        resize: resize ? null : 'none',
        pb: paddingHeight,
        ...inputBs,
      }"
      :context="{ bordered }"
      :value="modelValue"
      :placeholder="placeholder"
      :maxlength="maxlength"
      :rows="rowState"
      :disabled="disabled"
      :as="readonly ? 'div' : 'textarea'"
      v-bind="$attrs"
      @input="handleInput"
    >
      {{ modelValue }}
    </NewBsBox>

    <NewBsBox
      v-if="$slots.suffix || textMaxLength"
      :bs="{
        position: 'absolute', right: 0, left: 0, bottom: 0,
        mx: 12, textAlign: 'right', height: 22,
      }"
    >
      <slot name="suffix">
        <NewBsBox
          v-if="!readonly && textMaxLength"
          :bs="{
            color: exceedsLength ? 'danger' : 'ash',
            userSelect: 'none', fontFamily: 'pfRegular', fontSize: '14px',
          }"
        >
          {{ currentLen }}/{{ textMaxLength }}
        </NewBsBox>
      </slot>

    </NewBsBox>
  </NewBsBox>
</template>

<script lang="ts">
  import {
    defineComponent, PropType, ComponentPublicInstance,
    ref, computed,
  } from 'vue'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import initStyle from './style'
  import { BeerStyleObj } from '../../../core'
  import { useAutoExpand, getRows } from './rowUtils'
  import { FocusOption } from '../InputGroup/InputGroup.vue'

  initStyle()

  /**
   * 基本表单文本段落输入控件
   */
  export default defineComponent({
    name: 'TextArea',
    components: {
      NewBsBox,
    },
    inheritAttrs: false,
    props: {
      /**
       * 控制的值
       */
      modelValue: String,
      /**
       * @version 4.1.0
       * 是否带边框
       */
      bordered: {
        type: Boolean,
        default: true,
      },
      /**
       * @ignore
       *  容器 Bs
       */
      bs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({}),
      },
      /**
       * 是否只读显示
       */
      readonly: {
        type: Boolean,
        default: false,
      },
      /**
       * 初始行数
       */
      initialRows: {
        type: Number,
        default: 1,
      },
      /**
       * 是否允许手动操作 text area 高度
       */
      resize: {
        type: Boolean,
        default: true,
      },
      /**
       * 是否随着文字变化自动调整行数
       */
      autoExpand: {
        type: Boolean,
        default: false,
      },
      /**
       * 最小行数
       */
      minRows: {
        type: Number,
        default: 1,
      },
      /**
       * 是否禁止
       */
      disabled: {
        type: Boolean,
        default: false,
      },
      /**
       * 最大行数
       */
      maxRows: {
        type: Number,
        default: 6,
      },
      /**
       * @ignore
       * placeholder 的快捷 props，作为 inputProps 的默认值
       */
      placeholder: {
        type: String,
        default: '请输入',
      },
      /**
       * 显示字数限制
       */
      maxlength: {
        type: Number,
        default: undefined,
      },
      /**
       * @ignore
       * input tag bs
       */
      inputBs: {
        type: Object,
        default: () => ({}),
      },
    },
    emits: ['update:modelValue'],
    setup(props, { emit, slots }) {
      const inputRef = ref<ComponentPublicInstance|null>(null)
      const LINE_HEIGHT = 22
      const textMaxLength = computed(() => {
        if (props.disabled) return null
        return props.maxlength
      })
      const currentLen = computed(() => props.modelValue?.length || 0)
      const paddingHeight = computed(() => {
        if (!textMaxLength.value && !slots.suffix) {
          return 5
        }
        return (10 / 2) + LINE_HEIGHT
      })
      let rowState = ref(getRows(
        props.initialRows, props.minRows, props.maxRows,
      ))
      if (props.autoExpand) {
        const { rowState: expandRowState } = useAutoExpand({
          paddingHeight,
          inputRef,
          modelValue: computed(() => props.modelValue),
          initialRows: props.initialRows,
          minRows: props.minRows,
          maxRows: props.maxRows,
          lineHeight: LINE_HEIGHT,
        })
        rowState = expandRowState
      }
      const minTextAreaHeight = computed(() => (props.minRows * LINE_HEIGHT) + paddingHeight.value + 10)
      const exceedsLength = computed(() => {
        if (!textMaxLength.value) {
          return false
        }
        return currentLen.value > textMaxLength.value
      })

      return {
        rowState,
        inputRef,
        minTextAreaHeight,
        paddingHeight,
        textMaxLength,
        currentLen,
        exceedsLength,
        handleInput(e: KeyboardEvent) {
          emit('update:modelValue', (e.target as HTMLInputElement).value)
        },
        focus(option: FocusOption = {}) {
          if (inputRef.value) {
            const cmp = inputRef.value as ComponentPublicInstance
            const el = cmp.$el
            if (option.cursor === 'select') {
              el.setSelectionRange(0, el.value.length)
            }
            if (option.cursor === 'start') {
              el.setSelectionRange(0, 0)
            }
            if (option.cursor === 'end') {
              el.setSelectionRange(el.value.length, el.value.length)
            }
            el.focus()
          }
        },
        blur() {
          if (inputRef.value) {
            (inputRef.value as ComponentPublicInstance).$el.blur()
          }
        },
      }
    },
  })
</script>
